import React, {
    FormEvent, ReactElement, useEffect, useState,
} from "react";
import { Button, Modal } from "react-bootstrap";
import useKeyboardShortcuts, { useKeyboardState } from "../../contexts/KeyboardShortcutContext";

import "./Confirm.css";

interface Props {
    confirmText?: string;
    closeText?: string;
    children: ReactElement | string;
    size?: "sm" | "lg" | "xl";
    shouldShow: boolean;
    onHide?: () => void;
    onConfirm?: (event?: FormEvent) => void;
    className?: string;
    bodyClassName?: string;
    dialogClassName?: string;
    keyboardContext?: string;
    confirmActionText?: string;
    confirmVariant?: string;
    shouldCenter?: boolean;
    fullscreen?: string;
    shouldDisableEscKey?: boolean;
    shouldEnforceFocus?: boolean;
}
/**
 * props: variant- bootstrap variant to style Alert
 *        message
 * state: none
 */

const KEYBOARD_STATE = "confirm";
export default function Confirm(props: Props): ReactElement | null {
    const ref = React.createRef<HTMLFormElement>();
    const keyboardState = useKeyboardState();
    const [confirmActionText, setConfirmActionText] = useState("");

    // const ref = useMemo(() => React.createRef<HTMLFormElement>(), []);
    // const showValue = useMemo(() => ({
    //     shouldShow: props.shouldShow,
    // }), [props.shouldShow]);
    // showValue.shouldShow = props.shouldShow;

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "cmd+enter",
            context: props.keyboardContext ?? KEYBOARD_STATE,
            shouldUseAllContexts: true,
            handler: () => {
                if (!props.shouldShow) {
                    return;
                }
                if (ref.current?.checkValidity()) {
                    props.onConfirm?.();
                } else {
                    ref.current?.reportValidity();
                }
            },
        });
        shortcuts.on({
            shortcut: "esc",
            shouldUseAllContexts: true,
            context: props.keyboardContext ?? KEYBOARD_STATE,
            handler: () => {
                if (!props.shouldShow) {
                    return;
                }
                if (props.shouldDisableEscKey) {
                    return;
                }
                props.onHide?.();
            },
        });
    });

    useEffect(() => {
        if (props.shouldShow) {
            keyboardState.pushContext(props.keyboardContext ?? KEYBOARD_STATE);
        } else {
            keyboardState.popContext(props.keyboardContext ?? KEYBOARD_STATE);
        }
    }, [props.shouldShow]);
    const submit = (event: FormEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onConfirm?.(event);
        return false;
    };
    if (!props.shouldShow) {
        return null;
    }
    const shouldShowFooter = props.onHide
        || (props.confirmText && (!props.confirmActionText || props.confirmActionText === confirmActionText));
    return (
        <Modal
            show={props.shouldShow}
            // onHide={props.onHide}
            className="confirm-modal"
            size={props.size}
            contentClassName={`${props.className} h-100 shadow `}
            dialogClassName={props.dialogClassName}
            centered={props.shouldCenter}
            fullscreen={props.fullscreen}
            enforceFocus={props.shouldEnforceFocus}
        >
            <form
                onSubmit={props.confirmText ? submit : undefined}
                ref={ref}
                className="d-flex flex-column h-100 modal-max-height"
            >
                <Modal.Body className={`overflow-auto flex-grow-1 flex-shrink-1 ${props.bodyClassName}`}>
                    {props.children}

                    {props.confirmActionText && (
                        <div>
                            <small>
                                Please type
                                {" "}
                                <code>{props.confirmActionText}</code>
                                {" "}
                                in the box below to continue.
                            </small>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={props.confirmActionText}
                                onChange={event => setConfirmActionText(event.target.value)}
                            />
                        </div>
                    )}
                </Modal.Body>

                {shouldShowFooter && (
                    <Modal.Footer className="flex-grow-0">
                        {props.onHide && (
                            <Button
                                variant="secondary"
                                className="modal-btn"
                                onClick={props.onHide}
                            >
                                {props.closeText}
                            </Button>
                        )}

                        {(props.confirmText && (!props.confirmActionText || props.confirmActionText === confirmActionText)) && (
                            <Button
                                type="submit"
                                variant={props.confirmVariant}
                                className="model-btn"
                                data-test="publish-or-delete-btn"
                            >
                                {props.confirmText}
                            </Button>
                        )}

                    </Modal.Footer>
                )}

            </form>

        </Modal>
    );
}

Confirm.defaultProps = {
    closeText: "Close",
    confirmText: undefined,
    onConfirm: undefined,
    className: "",
    dialogClassName: "",
    bodyClassName: "",
    onHide: undefined,
    size: "",
    keyboardContext: undefined,
    confirmActionText: undefined,
    confirmVariant: "primary",
    shouldCenter: undefined,
    fullscreen: undefined,
    shouldDisableEscKey: undefined,
    shouldEnforceFocus: true,
};
